const resources = {
  en: {
    liabilityManager: {
      title: 'Liabilities',
      pageDescription: 'Track obligations, review outstanding balances, and record payments without leaving this workspace.',
      actions: {
        add: 'Add liability',
        edit: 'Edit',
        pay: 'Record payment',
        history: 'History',
        delete: 'Delete',
        cancel: 'Cancel',
        save: 'Save',
        confirmDeleteTitle: 'Delete this liability?',
      },
      notices: {
        dueDateTitle: 'Due dates create payment tasks',
        dueDateDescription: 'When a liability has a due date, the system can surface that date as a payment reminder task.',
      },
      summary: {
        count: {
          label: 'Liabilities',
          supporting: 'Active liability records in this workspace',
        },
        totalAmount: {
          label: 'Total amount',
          estimatedLabel: 'Estimated total amount',
          supporting: 'Original obligation volume',
        },
        totalPaid: {
          label: 'Total paid',
          estimatedLabel: 'Estimated total paid',
          supporting: 'Payments already recorded',
        },
        outstanding: {
          label: 'Outstanding',
          estimatedLabel: 'Estimated outstanding',
          supporting: '{{count}} liabilities still need payment',
        },
      },
      valuation: {
        estimatedTitle: 'Liability totals use explicit valuation',
        estimatedDescription: 'This summary values liability totals in {{currency}} from native obligation records and cached FX quotes.',
        staleTitle: 'Liability totals use stale FX quotes',
        staleDescription: 'This summary values liabilities in {{currency}}, but some FX quotes are stale. Totals may lag until quotes refresh.',
        degradedTitle: 'Liability totals are partially degraded',
        degradedDescription: 'This summary values liabilities in {{currency}}, but some FX quotes are missing or failed. Totals may be incomplete until refresh succeeds.',
        partialTitle: 'Liability totals are only partially valued',
        partialDescription: 'This summary values liabilities in {{currency}}, but some obligations cannot be valued with the current quote set.'
      },
      sections: {
        list: {
          title: 'Manage liabilities',
          description: 'Review obligations, inspect due dates, and act on each liability from one place.',
        },
      },
      list: {
        empty: 'No liabilities yet',
        defaultTitle: 'Liability',
        amount: 'Amount',
        paid: 'Paid',
        outstanding: 'Outstanding',
        date: 'Date: {{date}}',
        dueDate: 'Due: {{date}}',
        description: '{{description}}',
      },
      form: {
        addTitle: 'Add liability',
        editTitle: 'Edit liability',
        amount: 'Amount',
        amountHint: 'Decimals are supported. Use a dot, for example: 12.34',
        amountRequired: 'Enter the liability amount',
        date: 'Date',
        dateRequired: 'Select the liability date',
        dueDate: 'Due date',
        dueDateHint: 'A payment task can appear on this date',
        reason: 'Reason',
        description: 'Description',
      },
      paymentModal: {
        title: 'Record payment',
        amount: 'Amount',
        amountHint: 'Decimals are supported. Use a dot, for example: 12.34',
        amountRequired: 'Enter the payment amount',
        date: 'Date',
        dateRequired: 'Select the payment date',
        account: 'Account',
        accountRequired: 'Select an account',
        accountPlaceholder: 'Select account',
        note: 'Note',
      },
      history: {
        title: 'Payments: {{name}}',
        loading: 'Loading payments...',
        empty: 'No payments found.',
        itemDescription: '{{date}} • {{note}}',
        noNote: 'No note',
      },
      messages: {
        loadLiabilitiesError: 'Failed to load liabilities',
        loadAccountsError: 'Failed to load accounts',
        loadPaymentsError: 'Failed to load payments',
        created: 'Liability created',
        updated: 'Liability updated',
        deleted: 'Liability deleted',
        paymentRecorded: 'Payment recorded',
        errorWithReason: '{{error}}',
      },
    },
  },
  uk: {
    liabilityManager: {
      title: 'Зобов’язання',
      pageDescription: 'Відстежуйте зобов’язання, переглядайте непогашені залишки та фіксуйте платежі з одного простору.',
      actions: {
        add: 'Додати зобов’язання',
        edit: 'Редагувати',
        pay: 'Записати платіж',
        history: 'Історія',
        delete: 'Видалити',
        cancel: 'Скасувати',
        save: 'Зберегти',
        confirmDeleteTitle: 'Видалити це зобов’язання?',
      },
      notices: {
        dueDateTitle: 'Строк платежу створює нагадування',
        dueDateDescription: 'Коли у зобов’язання є строк, система може показати цю дату як задачу-нагадування про платіж.',
      },
      summary: {
        count: {
          label: 'Зобов’язання',
          supporting: 'Активні записи зобов’язань у цьому просторі',
        },
        totalAmount: {
          label: 'Загальна сума',
          estimatedLabel: 'Оціночна загальна сума',
          supporting: 'Початковий обсяг зобов’язань',
        },
        totalPaid: {
          label: 'Усього сплачено',
          estimatedLabel: 'Оціночно сплачено',
          supporting: 'Платежі, які вже зафіксовані',
        },
        outstanding: {
          label: 'Залишок',
          estimatedLabel: 'Оціночний залишок',
          supporting: '{{count}} зобов’язань ще потребують оплати',
        },
      },
      valuation: {
        estimatedTitle: 'Підсумки зобов’язань уже працюють на явній моделі оцінки',
        estimatedDescription: 'Цей підсумок оцінює зобов’язання у {{currency}} на основі нативних записів і кешованих FX-курсів.',
        staleTitle: 'Підсумки зобов’язань використовують застарілі FX-курси',
        staleDescription: 'Цей підсумок оцінює зобов’язання у {{currency}}, але частина FX-курсів застаріла. Підсумки можуть оновитися після refresh.',
        degradedTitle: 'Оцінка підсумків зобов’язань частково деградована',
        degradedDescription: 'Цей підсумок оцінює зобов’язання у {{currency}}, але частина FX-курсів відсутня або завершилася помилкою. Підсумки можуть бути неповними до успішного refresh.',
        partialTitle: 'Підсумки зобов’язань оцінено лише частково',
        partialDescription: 'Цей підсумок оцінює зобов’язання у {{currency}}, але частину записів неможливо оцінити з поточним набором курсів.'
      },
      sections: {
        list: {
          title: 'Керування зобов’язаннями',
          description: 'Переглядайте зобов’язання, строки та дії по кожному запису в одному місці.',
        },
      },
      list: {
        empty: 'Поки немає зобов’язань',
        defaultTitle: 'Зобов’язання',
        amount: 'Сума',
        paid: 'Сплачено',
        outstanding: 'Залишок',
        date: 'Дата: {{date}}',
        dueDate: 'Строк: {{date}}',
        description: '{{description}}',
      },
      form: {
        addTitle: 'Додати зобов’язання',
        editTitle: 'Редагувати зобов’язання',
        amount: 'Сума',
        amountHint: 'Дробова частина підтримується. Використовуйте крапку, наприклад: 12.34',
        amountRequired: 'Вкажіть суму зобов’язання',
        date: 'Дата',
        dateRequired: 'Оберіть дату зобов’язання',
        dueDate: 'Строк платежу',
        dueDateHint: 'У цей день може з’явитися задача на оплату',
        reason: 'Причина',
        description: 'Опис',
      },
      paymentModal: {
        title: 'Записати платіж',
        amount: 'Сума',
        amountHint: 'Дробова частина підтримується. Використовуйте крапку, наприклад: 12.34',
        amountRequired: 'Вкажіть суму платежу',
        date: 'Дата',
        dateRequired: 'Оберіть дату платежу',
        account: 'Рахунок',
        accountRequired: 'Оберіть рахунок',
        accountPlaceholder: 'Оберіть рахунок',
        note: 'Нотатка',
      },
      history: {
        title: 'Платежі: {{name}}',
        loading: 'Завантаження платежів...',
        empty: 'Платежів не знайдено.',
        itemDescription: '{{date}} • {{note}}',
        noNote: 'Без нотатки',
      },
      messages: {
        loadLiabilitiesError: 'Не вдалося завантажити зобов’язання',
        loadAccountsError: 'Не вдалося завантажити рахунки',
        loadPaymentsError: 'Не вдалося завантажити платежі',
        created: 'Зобов’язання створено',
        updated: 'Зобов’язання оновлено',
        deleted: 'Зобов’язання видалено',
        paymentRecorded: 'Платіж записано',
        errorWithReason: '{{error}}',
      },
    },
  },
}

export default resources
